import React, { useMemo, useEffect, useState } from 'react';
import { Component } from '@/types';
import { useTimeTrackerStore } from '@/stores/time-tracker';
import { TimerBar } from './TimerBar';
import { TimeEntryList } from './TimeEntryList';
import { TimeTrackerCalendarView } from './TimeTrackerCalendarView';
import { TimeTrackerReportsView } from './TimeTrackerReportsView';
import { TimeTrackerInvoiceView } from './TimeTrackerInvoiceView';
import { TimeTrackerProjectManager } from './TimeTrackerProjectManager';
import { TimeTrackerSettings } from './TimeTrackerSettings';
import { summarizeEntries, formatDuration, formatCurrency } from './timeTrackerUtils';
import type {
  TimeTrackerEntry,
  TimeTrackerProject,
  TimeTrackerClient,
  TimeTrackerTask,
  WritebackConfig,
  AutomationRule,
} from '@/types/pm-types';
import { useDataWriteback } from '@/hooks/useDataWriteback';
import { useAutomationRules } from '@/hooks/useAutomationRules';
import { pmTheme } from './pmTheme';

export interface TimeTrackerProps {
  component: Component;
  data: Record<string, unknown>[];
  isViewerMode: boolean;
}

type ViewMode = 'timer' | 'calendar' | 'reports' | 'invoice';

const VIEW_TABS: { key: ViewMode; label: string }[] = [
  { key: 'timer', label: 'Timer' },
  { key: 'calendar', label: 'Calendar' },
  { key: 'reports', label: 'Reports' },
  { key: 'invoice', label: 'Invoice' },
];

export const TimeTracker: React.FC<TimeTrackerProps> = ({ component, data, isViewerMode }) => {
  const properties = (component.properties || {}) as Record<string, unknown>;

  // Extract properties
  const viewMode = (properties.viewMode as ViewMode) || 'timer';
  const projects = (properties.projects as TimeTrackerProject[]) || [];
  const clients = (properties.clients as TimeTrackerClient[]) || [];
  const tasks = (properties.tasks as TimeTrackerTask[]) || [];
  const defaultBillable = (properties.defaultBillable as boolean) ?? true;
  const currency = (properties.currency as string) || 'USD';
  const accentColor = (properties.accentColor as string) || '#10b981';
  const showBillableToggle = (properties.showBillableToggle as boolean) ?? true;
  const showTags = (properties.showTags as boolean) ?? true;

  // Field mapping
  const rawFieldMapping = properties.fieldMapping as Record<string, string | undefined> | undefined;
  const fieldMapping = useMemo(() => ({
    id: 'id',
    description: 'description',
    projectId: 'projectId',
    taskId: 'taskId',
    startTime: 'startTime',
    endTime: 'endTime',
    duration: 'duration',
    billable: 'billable',
    tags: 'tags',
    date: 'date',
    ...rawFieldMapping,
  }), [rawFieldMapping]);

  // Writeback
  const writeback = properties.writebackConfig as WritebackConfig | undefined;
  const { updateRecord: _updateRecord } = useDataWriteback({
    writebackConfig: writeback,
    componentId: component.id,
  });

  // Automation rules
  const { handleDataEvent: _handleDataEvent } = useAutomationRules({
    rules: properties.automationRules as AutomationRule[] | undefined,
    writebackConfig: writeback,
    componentId: component.id,
    fieldMapping: fieldMapping as Record<string, string | undefined>,
  });

  // Store state
  const activeView = useTimeTrackerStore((s) => s.activeView);
  const setActiveView = useTimeTrackerStore((s) => s.setActiveView);
  const storeEntries = useTimeTrackerStore((s) => s.entries);
  const storeProjects = useTimeTrackerStore((s) => s.projects);

  // Sync component properties into store on mount
  useEffect(() => {
    const state = useTimeTrackerStore.getState();
    if (projects.length > 0 && state.projects.length === 0) {
      for (const p of projects) state.addProject(p);
    }
    if (clients.length > 0 && state.clients.length === 0) {
      for (const c of clients) state.addClient(c);
    }
    if (tasks.length > 0 && state.tasks.length === 0) {
      for (const t of tasks) state.addTask(t);
    }
  }, [clients, projects, tasks]);

  // Map data-service records to TimeTrackerEntry format
  const dataEntries: TimeTrackerEntry[] = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map((record) => ({
      id: String(record[fieldMapping.id] ?? record.id ?? ''),
      description: String(record[fieldMapping.description] ?? record.description ?? ''),
      projectId: record[fieldMapping.projectId] as string | undefined ?? record.projectId as string | undefined,
      taskId: record[fieldMapping.taskId ?? 'taskId'] as string | undefined,
      startTime: String(record[fieldMapping.startTime] ?? record.startTime ?? ''),
      endTime: record[fieldMapping.endTime ?? 'endTime'] ? String(record[fieldMapping.endTime ?? 'endTime']) : undefined,
      duration: Number(record[fieldMapping.duration] ?? record.duration ?? 0),
      billable: Boolean(record[fieldMapping.billable ?? 'billable'] ?? false),
      tags: (record[fieldMapping.tags ?? 'tags'] as string[]) || [],
      date: String(record[fieldMapping.date] ?? record.date ?? ''),
    }));
  }, [data, fieldMapping]);

  // Merge store entries with data-service entries (store entries take priority)
  const mergedEntries = useMemo(() => {
    const storeIds = new Set(storeEntries.map((e) => e.id));
    const fromData = dataEntries.filter((e) => !storeIds.has(e.id));
    return [...storeEntries, ...fromData];
  }, [storeEntries, dataEntries]);

  // Use store projects if available, otherwise component properties
  const mergedProjects = storeProjects.length > 0 ? storeProjects : projects;

  // Summary
  const summary = useMemo(
    () => summarizeEntries(mergedEntries, mergedProjects, tasks),
    [mergedEntries, mergedProjects, tasks]
  );

  // Set initial view
  useEffect(() => {
    if (viewMode && activeView !== viewMode) {
      setActiveView(viewMode);
    }
  }, [activeView, setActiveView, viewMode]);

  const currentView = activeView || viewMode;

  // Project Manager & Settings modals
  const [showProjectManager, setShowProjectManager] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  return (
    <div
      data-testid="time-tracker"
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: pmTheme.surface,
        color: pmTheme.text,
        borderRadius: 8,
        overflow: 'hidden',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {/* Header with stats and view tabs */}
      <div style={{ borderBottom: `1px solid ${pmTheme.border}` }}>
        {/* Stats bar */}
        <div style={{ display: 'flex', gap: 24, padding: '10px 16px', background: pmTheme.surfaceSubtle, borderBottom: `1px solid ${pmTheme.borderSubtle}` }}>
          <div style={{ fontSize: 12, color: pmTheme.textMuted }}>
            <span style={{ fontWeight: 600, color: pmTheme.text, fontSize: 14 }}>{formatDuration(summary.totalSeconds)}</span>
            <span style={{ marginLeft: 4 }}>total</span>
          </div>
          {showBillableToggle && (
            <>
              <div style={{ fontSize: 12, color: pmTheme.textMuted }}>
                <span style={{ fontWeight: 600, color: '#10b981', fontSize: 14 }}>{formatDuration(summary.billableSeconds)}</span>
                <span style={{ marginLeft: 4 }}>billable</span>
              </div>
              <div style={{ fontSize: 12, color: pmTheme.textMuted }}>
                <span style={{ fontWeight: 600, color: pmTheme.text, fontSize: 14 }}>{formatCurrency(summary.totalBillable, currency)}</span>
                <span style={{ marginLeft: 4 }}>earned</span>
              </div>
            </>
          )}
        </div>

        {/* View tabs */}
        <div style={{ display: 'flex', gap: 0 }}>
          {VIEW_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveView(tab.key)}
              style={{
                flex: 1,
                padding: '10px 16px',
                background: currentView === tab.key ? pmTheme.surface : 'transparent',
                border: 'none',
                borderBottom: currentView === tab.key ? `2px solid ${accentColor}` : '2px solid transparent',
                color: currentView === tab.key ? accentColor : pmTheme.textMuted,
                fontWeight: currentView === tab.key ? 600 : 400,
                fontSize: 13,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {tab.label}
            </button>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, paddingRight: 8 }}>
            <button
              onClick={() => setShowProjectManager(true)}
              title="Manage Projects & Clients"
              style={{ background: 'none', border: 'none', fontSize: 16, color: pmTheme.textSubtle, cursor: 'pointer', padding: '4px 6px' }}
            >
              &#9776;
            </button>
            <button
              onClick={() => setShowSettings(true)}
              title="Settings"
              style={{ background: 'none', border: 'none', fontSize: 16, color: pmTheme.textSubtle, cursor: 'pointer', padding: '4px 6px' }}
            >
              &#9881;
            </button>
          </div>
        </div>
      </div>

      {/* View content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {currentView === 'timer' && (
          <>
            <TimerBar
              projects={mergedProjects}
              defaultBillable={defaultBillable}
              accentColor={accentColor}
            />
            <TimeEntryList
              entries={mergedEntries}
              projects={mergedProjects}
              isViewerMode={false}
              currency={currency}
            />
          </>
        )}

        {currentView === 'calendar' && (
          <TimeTrackerCalendarView
            entries={mergedEntries}
            projects={mergedProjects}
            accentColor={accentColor}
          />
        )}

        {currentView === 'reports' && (
          <TimeTrackerReportsView
            entries={mergedEntries}
            projects={mergedProjects}
            clients={clients}
            tasks={tasks}
            currency={currency}
            accentColor={accentColor}
          />
        )}

        {currentView === 'invoice' && (
          <TimeTrackerInvoiceView
            entries={mergedEntries}
            projects={mergedProjects}
            clients={clients}
            tasks={tasks}
            currency={currency}
          />
        )}
      </div>

      {/* Project Manager Modal */}
      <TimeTrackerProjectManager
        open={showProjectManager}
        onOpenChange={setShowProjectManager}
        accentColor={accentColor}
        currency={currency}
      />

      {/* Settings Modal */}
      {showSettings && (
        <TimeTrackerSettings
          onClose={() => setShowSettings(false)}
          defaultBillable={defaultBillable}
          timeRounding={(properties.timeRounding as number) || 1}
          currency={currency}
          accentColor={accentColor}
          onSave={() => {
            // Settings are applied via component properties — in a full implementation,
            // this would update component properties via the editor store
            setShowSettings(false);
          }}
        />
      )}
    </div>
  );
};

TimeTracker.displayName = 'TimeTracker';
